// TourPanOverlay.js
// Overlay used during the guided tour. It allows the user to pan/tilt
// the camera (look around) by dragging — one-finger touch on mobile,
// or left-mouse drag on desktop — while blocking sweep navigation
// (taps/clicks, multi-touch pinches, scroll-wheel zoom, etc.) that
// would otherwise jump the user to a different point in the model.
//
// Implementation:
//   - Covers the iframe so Matterport never sees raw input.
//   - Drag deltas (touch or mouse) are converted to degrees of
//     horizontal/vertical rotation and forwarded to sdk.Camera.rotate.
//   - Multi-touch, taps/clicks (without drag), and wheel events are
//     swallowed.

import React, { useEffect, useRef } from 'react';
import matterportService from '../services/matterportService';
import audioService from '../services/audioService';
import { Z_INDEX } from '../const';

// Sensitivity: how many degrees of camera rotation per pixel of drag.
// Tuned to roughly match Matterport's native pan feel on mobile.
const DEG_PER_PX_X = 0.18;
const DEG_PER_PX_Y = 0.18;

const TourPanOverlay = () => {
  const ref = useRef(null);
  const stateRef = useRef({ active: false, lastX: 0, lastY: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onTouchStart = (e) => {
      // Treat every tour-overlay touch as a fresh user gesture so the
      // mobile Web Audio context stays unlocked. iOS Safari suspends the
      // context aggressively when the page is idle for a while, which
      // would otherwise silence narration mid-tour.
      try { audioService.unlockOnGesture?.(); } catch (_) {}

      // Only single-finger drags pan the camera. Multi-touch (pinch /
      // two-finger pan) is swallowed to prevent zoom/sweep navigation.
      if (e.touches.length === 1) {
        const t = e.touches[0];
        stateRef.current.active = true;
        stateRef.current.lastX = t.clientX;
        stateRef.current.lastY = t.clientY;
      } else {
        stateRef.current.active = false;
      }
      e.preventDefault();
    };

    const onTouchMove = (e) => {
      if (!stateRef.current.active || e.touches.length !== 1) {
        e.preventDefault();
        return;
      }
      const t = e.touches[0];
      const dx = t.clientX - stateRef.current.lastX;
      const dy = t.clientY - stateRef.current.lastY;
      stateRef.current.lastX = t.clientX;
      stateRef.current.lastY = t.clientY;

      const sdk = matterportService?.state?.sdk;
      if (sdk && sdk.Camera && typeof sdk.Camera.rotate === 'function') {
        // Matterport's native touch model is "grab the world and drag":
        //   • finger drag right → world moves right → view rotates LEFT  (negate dx)
        //   • finger drag down  → world moves down  → view tilts  DOWN  (positive dy)
        // The vertical sign here is positive so up/down feels the same
        // as outside the guided tour (drag up = look up).
        const horizontal = -dx * DEG_PER_PX_X;
        const vertical = dy * DEG_PER_PX_Y;
        if (horizontal !== 0 || vertical !== 0) {
          sdk.Camera.rotate(horizontal, vertical, { speed: 100 }).catch(() => {});
        }
      }
      e.preventDefault();
    };

    const onTouchEnd = (e) => {
      stateRef.current.active = false;
      e.preventDefault();
    };

    // ── Desktop mouse drag ──
    // Mouse events don't fire on touch devices for taps, so adding these
    // handlers is safe alongside the touch handlers above. Left-button
    // drag rotates the camera using the same DEG_PER_PX_* feel.
    const rotateBy = (dx, dy) => {
      const sdk = matterportService?.state?.sdk;
      if (!sdk || !sdk.Camera || typeof sdk.Camera.rotate !== 'function') return;
      const horizontal = -dx * DEG_PER_PX_X;
      const vertical = dy * DEG_PER_PX_Y;
      if (horizontal !== 0 || vertical !== 0) {
        sdk.Camera.rotate(horizontal, vertical, { speed: 100 }).catch(() => {});
      }
    };

    const onMouseDown = (e) => {
      if (e.button !== 0) return; // left button only
      stateRef.current.active = true;
      stateRef.current.lastX = e.clientX;
      stateRef.current.lastY = e.clientY;
      el.style.cursor = 'grabbing';
      e.preventDefault();
    };

    const onMouseMove = (e) => {
      if (!stateRef.current.active) return;
      const dx = e.clientX - stateRef.current.lastX;
      const dy = e.clientY - stateRef.current.lastY;
      stateRef.current.lastX = e.clientX;
      stateRef.current.lastY = e.clientY;
      rotateBy(dx, dy);
    };

    const onMouseUp = () => {
      stateRef.current.active = false;
      el.style.cursor = 'grab';
    };

    // Swallow clicks so a stray click doesn't reach Matterport (which
    // would attempt sweep navigation). The drag itself is handled above.
    const onClick = (e) => { e.preventDefault(); e.stopPropagation(); };

    // Native listeners with passive:false so preventDefault() works
    // on iOS (React's synthetic touch events are passive by default).
    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: false });
    el.addEventListener('touchcancel', onTouchEnd, { passive: false });

    // Mouse handlers — mousedown on the overlay, but move/up listen on
    // window so a drag that leaves the overlay still tracks correctly.
    el.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    el.addEventListener('click', onClick, true);

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
      el.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      el.removeEventListener('click', onClick, true);
    };
  }, []);

  return (
    <div
      ref={ref}
      aria-hidden="true"
      style={{
        position: 'absolute', inset: 0,
        zIndex: Z_INDEX.TOUR_SLIDESHOW,
        background: 'transparent',
        touchAction: 'none',
        cursor: 'grab',
      }}
      onContextMenu={(e) => e.preventDefault()}
      onWheelCapture={(e) => { e.preventDefault(); e.stopPropagation(); }}
    />
  );
};

export default TourPanOverlay;
